import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  KibSuperSelect,
  type KibSuperSelectOption,
} from '@chewy/kib-fields-new-react'
import { Defaults, moment, type Nil } from '@pbt/pbt-ui-components'

import { editConversation } from '~/store/actions/conversations'
import { fetchUpcomingEvents } from '~/store/actions/scheduler'
import { getConversationsError } from '~/store/reducers/conversations'
import {
  getSchedulerIsLoading,
  getSchedulerUpcomingAppointmentsList,
} from '~/store/reducers/scheduler'
import { getMultipleTimetableEvents } from '~/store/reducers/timetable'
import type { TimetableEvent } from '~/types'
import { convertToKibSuperSelectOption } from '~/utils/communicationsUtils'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import { useChatContainer } from '../context/ChatContainerContext'

import '@chewy/kib-fields-new-styles/dist/kib-fields-new.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'
import styles from './LinkToAppointmentSelect.module.scss'

interface LinkToAppointmentSelectProps {
  clientId: string | Nil
  conversationId: string
  currentAppointment?: TimetableEvent | Nil
  disabled?: boolean
  patientId: string | Nil
  patientName?: string
}

const LinkToAppointmentSelect = ({
  clientId,
  patientId,
  conversationId,
  currentAppointment,
  disabled = false,
  patientName = '',
}: LinkToAppointmentSelectProps) => {
  const { t } = useTranslation(['Dialogs'])
  const dispatch = useDispatch()
  const businessTimezone = useCurrentBusinessTimezone()

  const { pendingSelection, setPendingSelection } = useChatContainer()

  const isLoadingAppointments = useSelector(getSchedulerIsLoading)
  const appointmentsList =
    useSelector(getSchedulerUpcomingAppointmentsList) ?? []
  const upcomingEvents = useSelector(
    getMultipleTimetableEvents(appointmentsList),
  )
  const conversationError = useSelector(getConversationsError)

  const appointments = useMemo(() => {
    const validEvents = upcomingEvents.filter(Boolean)
    const upcomingOptions = convertToKibSuperSelectOption(validEvents)

    if (currentAppointment?.id) {
      const isCurrentInUpcoming = upcomingOptions.some(
        (opt) => opt.value === currentAppointment.id,
      )
      if (!isCurrentInUpcoming) {
        const currentOption = convertToKibSuperSelectOption([
          currentAppointment,
        ])[0]
        return [...upcomingOptions, currentOption]
      }
    }

    return upcomingOptions
  }, [upcomingEvents, currentAppointment])

  useEffect(() => {
    if (pendingSelection && currentAppointment?.id === pendingSelection.value) {
      setPendingSelection(null)
    }
  }, [currentAppointment?.id, pendingSelection])

  useEffect(() => {
    if (pendingSelection && conversationError) {
      setPendingSelection(null)
    }
  }, [conversationError, pendingSelection])

  const selectedValue = useMemo(() => {
    if (pendingSelection) {
      return [pendingSelection]
    }

    if (!currentAppointment?.id) return []
    return appointments.filter(
      (appointment) => appointment.value === currentAppointment.id,
    )
  }, [currentAppointment?.id, appointments, pendingSelection])

  useEffect(() => {
    if (clientId && patientId) {
      dispatch(
        fetchUpcomingEvents(
          clientId,
          patientId,
          moment
            .tz(businessTimezone)
            .subtract(Defaults.MAX_PAST_DAYS_FOR_APPOINTMENT, 'days')
            .format('YYYY-MM-DDTHH:mmZ'),
        ),
      )
    }
  }, [clientId, patientId, businessTimezone, dispatch])

  const handleChange = (selected: KibSuperSelectOption[]) => {
    const selectedOption = selected[0]
    const selectedAppointmentId = selectedOption?.value

    if (
      selectedAppointmentId &&
      selectedAppointmentId !== currentAppointment?.id
    ) {
      setPendingSelection(selectedOption)

      dispatch(
        editConversation(conversationId, {
          eventId: selectedAppointmentId,
        }),
      )
    }
  }

  const noAppointments = appointments.length === 0
  const isSaving = Boolean(pendingSelection)
  const isLoading = isLoadingAppointments || isSaving

  const loadingPlaceholder = t(
    'Dialogs:NEW_CONVERSATION_WITH_STEPS_DIALOG.LINK_TO_APPOINTMENT_PLACEHOLDER',
  )

  const loadedPlaceholder = noAppointments
    ? t(
        'Dialogs:NEW_CONVERSATION_WITH_STEPS_DIALOG.PATIENT_HAS_NO_APPOINTMENTS',
        { patientName },
      )
    : t(
        'Dialogs:NEW_CONVERSATION_WITH_STEPS_DIALOG.LINK_TO_APPOINTMENT_PLACEHOLDER',
      )

  const placeholder = isLoadingAppointments
    ? loadingPlaceholder
    : loadedPlaceholder

  return (
    <KibSuperSelect
      className={styles.appointmentSelector}
      disabled={disabled || isLoadingAppointments || noAppointments}
      label={t(
        'Dialogs:NEW_CONVERSATION_WITH_STEPS_DIALOG.LINK_TO_APPOINTMENT',
      )}
      loading={isLoading}
      options={appointments}
      placeholder={placeholder}
      value={selectedValue}
      onChange={handleChange}
    />
  )
}

export default LinkToAppointmentSelect
